/**
 * DataListSearchForm — FORM TÌM KIẾM RIÊNG của DataListManager (← `search_form` của listinfo).
 *
 * Khác DataListFilter (lọc nâng cao dựng từ chính các cột của bản ghi): form này có bộ field RIÊNG, mỗi
 * field tự biết dịch sang điều kiện (script `searchCondition` ← `handle_search_condition`). Luật dịch
 * nằm ở searchFormRules.js (thuần, có test).
 *
 * Hỗ trợ đủ phương ngữ field của form nhập: `hideInForm`/`readOnly` dạng script, `default` dạng script
 * (chạy MỘT lần khi mở lần đầu), `onValueChanged` (script sửa thẳng vào dữ liệu form), `required`.
 */
import React, { useState, useEffect, useContext, useRef } from 'react';
import { Box, Typography, IconButton, Button, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Grid from './ContainerGrid';
import ManagedModal from './ManagedModal';
import BaseFieldControl from './BaseFieldControl';
import AuthContext from './auth/Context';
import Moment from 'moment';
import * as utils from '../utils';
import { numeral as Numeral } from '../utils';
import { getDateRangeByType } from './DateFilterInput';
import { resolveFieldDefaults } from './fieldDefaults';
import { buildValueChangedArgs } from './lifecycleHooks';
import { resolveRequired, coerceFieldValue } from './formRules';
import { searchFormInitialValues, isEmptySearchValue } from './searchFormRules';

const IOS_BLUE = '#007aff';
const DEFAULT_DEPS = { Moment, utils };

export default function DataListSearchForm({
  open, onClose, fields = [], title = 'Tìm kiếm', values, onSearch, onClear,
  required = false,   // searchRequired: không cho đóng khi chưa có điều kiện
  masterData,
}) {
  const context = useContext(AuthContext);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});
  const initedRef = useRef(false);

  // Mở form: lấy lại điều kiện đang áp; lần ĐẦU (chưa từng tìm) thì tính giá trị mặc định — gồm cả
  // script `default` bất đồng bộ (vd kỳ hiện tại, kho mặc định của người dùng).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      if (values && Object.keys(values).length) { setData({ ...values }); return; }
      if (initedRef.current) return;
      initedRef.current = true;
      const base = searchFormInitialValues(fields, { getDateRangeByType });
      let scripted = {};
      try {
        const token = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '');
        scripted = await resolveFieldDefaults(
          fields.filter(f => f.type !== 'DateFilter'),
          { master: masterData, context, token, mediaQueryMatches: !isDesktop },
          DEFAULT_DEPS,
        );
      } catch (e) {
        console.error('[DataListSearchForm] giá trị mặc định lỗi:', (e && e.message) || e);
      }
      if (!cancelled) setData({ ...base, ...scripted });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const evalFlag = (flag) => {
    if (typeof flag === 'function') {
      try { return !!flag(context, data, masterData || data, fields); } catch (e) { return false; }
    }
    return flag === true;
  };

  const handleChange = async (f, raw, optionObj) => {
    const newValue = coerceFieldValue(f, raw);
    const oldValue = data[f.field];
    let next = { ...data, [f.field]: newValue };
    // Ô chọn từ danh mục: cất nhãn vào `label_as` như form nhập — script tìm kiếm có thể cần.
    if ((f.type === 'Autocomplete' || f.type === 'Select') && optionObj && f.apiMapping?.label_as && f.apiMapping.label_as !== f.field) {
      next[f.apiMapping.label_as] = Array.isArray(optionObj) ? optionObj.map(o => o.label) : optionObj.label;
    }
    if (typeof f.onValueChanged === 'function') {
      try {
        let updates = f.onValueChanged(...buildValueChangedArgs({
          field: f.field, newValue, oldValue, optionObj, formData: next, master: masterData || next,
          oldData: data, fields, context, utils, moment: Moment, numeral: Numeral,
        }));
        if (updates && typeof updates.then === 'function') updates = await updates;
        if (updates && typeof updates === 'object') next = { ...next, ...updates };
      } catch (e) {
        console.error(`[DataListSearchForm] onValueChanged của "${f.field}" lỗi:`, (e && e.message) || e);
      }
    }
    setData(next);
    if (errors[f.field]) setErrors(prev => { const n = { ...prev }; delete n[f.field]; return n; });
  };

  const handleSearch = () => {
    const errs = {};
    for (const f of fields) {
      if (evalFlag(f.hideInForm)) continue;
      if (resolveRequired(f, { context, formData: data, masterData: masterData || data, fields }) && isEmptySearchValue(data[f.field])) {
        errs[f.field] = `${f.label || f.field} là bắt buộc`;
      }
    }
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSearch(data);
    onClose();
  };

  const handleClear = () => {
    setData(searchFormInitialValues(fields, { getDateRangeByType }));
    setErrors({});
    onClear();
    if (!required) onClose();
  };

  return (
    <ManagedModal
      type="drawer"
      anchor={isDesktop ? 'right' : 'bottom'}
      open={open}
      onClose={required ? () => {} : onClose}
      PaperProps={{
        sx: isDesktop
          ? { width: 460, maxWidth: '100vw', borderRadius: 3, m: 2, height: 'auto', maxHeight: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }
          : { borderTopLeftRadius: 24, borderTopRightRadius: 24, pb: 'max(24px, env(safe-area-inset-bottom))', maxHeight: '90vh', width: '100%', display: 'flex', flexDirection: 'column' }
      }}
    >
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">{title}</Typography>
          {!required && <IconButton onClick={onClose} size="small" sx={{ bgcolor: 'action.hover' }}><CloseIcon /></IconButton>}
        </Box>
        <Box
          sx={{ overflowY: 'auto', flexGrow: 1, minHeight: 0, pt: 1, px: 0.5, mx: -0.5 }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && e.target?.tagName === 'INPUT') handleSearch(); }}
        >
          <Grid container spacing={2}>
            {fields.map(f => {
              if (evalFlag(f.hideInForm)) return null;
              const size = f.gridFilter || f.gridEdit || { xs: 12 };
              return (
                <Grid size={size} key={f.field}>
                  <BaseFieldControl
                    field={{ ...f, required: resolveRequired(f, { context, formData: data, masterData: masterData || data, fields }) }}
                    value={data[f.field]}
                    onChange={(val, optionObj) => handleChange(f, val, optionObj)}
                    formData={data}
                    masterData={masterData}
                    formFields={fields}
                    isReadOnly={evalFlag(f.readOnly)}
                    error={!!errors[f.field]}
                  />
                  {errors[f.field] && <Typography variant="caption" color="error">{errors[f.field]}</Typography>}
                </Grid>
              );
            })}
          </Grid>
        </Box>
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #f0f0f0', display: 'flex', gap: 1.5, flexShrink: 0 }}>
          <Button variant="outlined" color="inherit" fullWidth onClick={handleClear} sx={{ borderRadius: 2, textTransform: 'none' }}>
            Xoá điều kiện
          </Button>
          <Button variant="contained" fullWidth onClick={handleSearch} sx={{ borderRadius: 2, bgcolor: IOS_BLUE, textTransform: 'none' }}>
            Tìm kiếm
          </Button>
        </Box>
      </Box>
    </ManagedModal>
  );
}
